/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { loadActivityFieldsTypes, loadPartnerActivityFields, savePartnerActivityFields } from '../actionCreators';
import { connect, useDispatch } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { getOfferActivityFields, getActivityFieldsIsLoaded } from '../selectors';
import { Prompt } from 'react-router-dom';

import ActivityFieldsComponent from './ActivityFieldsComponent';

import withCustomComponent from 'providers/withCustomComponent';
import { shape, required, items } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';

const ActivityFieldsContainer = ({ documentId, siteId, partnerId, actions, activityFieldsLoaded, activityFields, t }) => {
  const dispatch = useDispatch();
  const [isDirty, setIsDirty] = useState(false);
  const [localActivityFields, setLocalActivityFieldsList] = useState(activityFields || []);
  const [activityDomainsCardExpanded, setActivityDomainsCardExpanded] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [totalPercentageValid, setTotalPercentageValid] = useState(true);
  const [saving, setSaving] = useState(false);

  const [validDefaultActivitySelection, setValidDefaultActivitySelection] = useState(true);
  const [validActivityFieldMultipleSelection, setValidActivityFieldMultipleSelection] = useState(true);

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const validatorActivityField = items(
    shape({
      activityFieldId: required
    })
  );
  const [validationActivityField, validateActivityField] = useDirtyFieldValidation(validatorActivityField, dirtyInfo);

  const onActivityDomainsCardExpanded = (_, expanded) => {
    setActivityDomainsCardExpanded(!expanded);
  };

  useEffect(() => {
    if (!activityFieldsLoaded) {
      actions.loadActivityFieldsTypes();
      if (partnerId !== null) actions.loadPartnerActivityFields(documentId, siteId, partnerId);
    }
  }, [activityDomainsCardExpanded, activityFieldsLoaded]);

  useEffect(() => {
    if (partnerId !== null) actions.loadPartnerActivityFields(documentId, siteId, partnerId);
  }, []);

  useEffect(() => {
    if (activityFieldsLoaded) setLocalActivityFieldsList(activityFields);
  }, [activityFieldsLoaded]);

  const addActivityField = () => {
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);

    let localActivityFieldsList = [...localActivityFields, {}];
    setLocalActivityFieldsList(localActivityFieldsList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const onActivityFieldPropertyChange = idx => (prop1, prop2) => value => {
    setDirtyInfo(`${prop1}`);
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);

    const isActivityFieldId = prop1 === 'activityFieldId';

    const propertyId = prop1;
    const propertyName = prop2;
    const propertyIdValue = value?.['value'] ?? value;
    const propertyNameValue = isActivityFieldId ? value?.['label']?.substr(value['label'].indexOf(' ') + 1) : value?.['label'];

    const activityFieldCode = isActivityFieldId ? value?.['label']?.substr(0, value['label'].indexOf(' ')) : null;

    setLocalActivityFieldsList(
      Object.assign([], {
        ...localActivityFields,
        [idx]: {
          ...localActivityFields[idx],
          [propertyId]: propertyIdValue,
          ...(propertyName && { [propertyName]: propertyNameValue }),
          ...(activityFieldCode && { activityFieldCode: activityFieldCode })
        }
      })
    );

    setSaveDisabled(false);
    setIsDirty(true);
  };

  const deleteActivityField = idx => {
    setValidDefaultActivitySelection(true);
    setValidActivityFieldMultipleSelection(true);
    let newActivityFieldsList = localActivityFields.filter(x => localActivityFields.indexOf(x) !== idx);
    setLocalActivityFieldsList(newActivityFieldsList);
    setSaveDisabled(false);
    setIsDirty(true);
  };

  const isTotalPercentageValid = localActivityFields => (min, max) => {
    const totalPercentage = localActivityFields.reduce((totalPercentage, activityField) => {
      return isNaN(Number(activityField.weightPercent)) ? totalPercentage : totalPercentage + Number(activityField.weightPercent);
    }, 0);
    if (min <= totalPercentage && totalPercentage <= max) return true;
    return false;
  };

  const validateLocalActivityFields_defaultActivity = localActivityFields => {
    let counter = 0;
    localActivityFields.map(activityField => {
      if (activityField.defaultActivity === true) {
        counter++;
      }
      return counter;
    });
    return counter;
  };

  const saveActivityFields = (documentId, siteId, partnerId, localActivityFields) => {
    const totalPercentageValid = isTotalPercentageValid(localActivityFields)(0, 100);
    setTotalPercentageValid(totalPercentageValid);

    if (!validateActivityField(localActivityFields) || !totalPercentageValid) {
      return;
    }
    const counter = validateLocalActivityFields_defaultActivity(localActivityFields);
    if (counter > 1) {
      setValidDefaultActivitySelection(false);
    } else if (localActivityFields.length !== new Set(localActivityFields.map(a => a.activityFieldId)).size) {
      setValidActivityFieldMultipleSelection(false);
    } else {
      setValidDefaultActivitySelection(true);
      setSaving(true);

      actions
        .savePartnerActivityFields(documentId, siteId, partnerId, localActivityFields, () => {
          setIsDirty(false);
        })
        .then(() => setSaving(false));

      setSaveDisabled(true);
    }
  };

  useEffect(() => {
    validateActivityField(localActivityFields, dirtyInfo);
  }, [localActivityFields, dirtyInfo]);

  const loadActivityFields = search => {
    if (!search) return Promise.resolve([]);

    return dispatch(loadActivityFieldsTypes(search)).then(data => {
      return data.map(item => {
        return { value: item.id, label: item.code + ' ' + item.name };
      });
    });
  };
  return (
    <>
      <Prompt when={isDirty} message={t('LeavingWithoutSaving')} />
      <ActivityFieldsComponent
        documentId={documentId}
        siteId={siteId}
        partnerId={partnerId}
        onActivityDomainsCardExpanded={onActivityDomainsCardExpanded}
        activityDomainsCardExpanded={activityDomainsCardExpanded}
        saveDisabled={saveDisabled}
        addActivityField={addActivityField}
        loadActivityFieldsTypes={loadActivityFields}
        onPropertyChange={onActivityFieldPropertyChange}
        activityFields={localActivityFields}
        deleteActivityField={deleteActivityField}
        saveActivityFields={saveActivityFields}
        totalPercentageValid={totalPercentageValid}
        validDefaultActivitySelection={validDefaultActivitySelection}
        validActivityFieldMultipleSelection={validActivityFieldMultipleSelection}
        validationActivityField={validationActivityField}
        saving={saving}
      />
    </>
  );
};

ActivityFieldsContainer.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  activityFields: PropTypes.array,
  activityFieldsLoaded: PropTypes.bool.isRequired,
  documentId: PropTypes.number,
  siteId: PropTypes.number
};

function mapStateToProps(state, ownProps) {
  return {
    activityFields: getOfferActivityFields(state, ownProps.partnerId),
    activityFieldsLoaded: getActivityFieldsIsLoaded(state, ownProps.partnerId)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadActivityFieldsTypes,
        loadPartnerActivityFields,
        savePartnerActivityFields
      },
      dispatch
    ),
    dispatch
  };
}
export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withCustomComponent('ActivityFieldsContainer')
)(withTranslation()(ActivityFieldsContainer));
